'use client';
import {
    createContext,
    useContext,
    ReactNode,
    useState,
    useMemo,
    useEffect,
    useCallback,
} from 'react';
import { useQuery, ApolloClient } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import {
    GET_RESOURCE_LIBRARY_ASSET_ITEMS_QUERY,
    getResourceLibraryAssetItemsQueryVariables,
} from '../queries/getResourceLibraryAssetItemsQuery';
import {
    GET_RESOURCE_LIBRARY_INTERNAL_LINK_ITEMS_QUERY,
    getResourceLibraryInternalLinkItemsQueryVariables,
} from '../queries/getResourceLibraryInternalLinkItemsQuery';

import { ResourceLibraryItem } from '../types/resourceLibraryItems';
import { transformResourceLibraryAssetItemsResponse } from '../tools/transformResourceLibraryAssetItemsResponse';
import { transformResourceLibraryInternalLinkItemsResponse } from '../tools/transformResourceLibraryInternalLinkItemsResponse';
import { GetResourceLibraryAssetItemListingQuery } from '../schemas/generated-types/graphql';
import { GetResourceLibraryInternalLinkItemListingQuery } from '../schemas/generated-types/graphql';

// Import the unsafe versions so we do not trigger initialization
import { useProductGroupsUnsafe } from './ProductGroupsProvider';
import { useProductApplicationsUnsafe } from './ProductApplicationsProvider';
import { useResourceTypesUnsafe } from './ResourceTypesProvider';
import { useNavigationNodesUnsafe } from '../../siteNavigation/contexts/NavigationNodesProvider';
import { useResourceLibraryArticleItemsUnsafe } from './ResourceLibraryArticleItemsProvider';
import { useResourceLibraryNewsItemsUnsafe } from './ResourceLibraryNewsItemsProvider';
import { useResourceLibraryNewProductIntroductionItemsUnsafe } from './ResourceLibraryNewProductIntroductionItemsProvider';
import { useResourceLibrarySitePageItemsUnsafe } from './ResourceLibrarySitePageItemsProvider';

import { Filters, FilterName, filterDescriptors } from '../types/filters';
import {
    toggleValueInArray,
    filterItemsWithRelevance,
    filterItemsUsingEmptyFilters,
} from './filters';
import { SortOrder } from '../types/sorting';
import { SearchResult } from '../types/search';
import { getCounts, Counts } from './counts';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

type FilteredItemsRelevanceMap = Map<string, SearchResult>;

interface ResourceLibraryItemContextValue {
    items: ResourceLibraryItem[];
    filteredItems: ResourceLibraryItem[];
    filteredItemsRelevanceMap: FilteredItemsRelevanceMap;
    paginatedItems: ResourceLibraryItem[];
    loading: boolean;
    error: any;
    filters: Filters;
    setFilter: <K extends FilterName>(filterName: K, value: Filters[K]) => void;
    setFilterSync: <K extends FilterName>(
        filterName: K,
        value: Filters[K]
    ) => ResourceLibraryItem[]; // 🔸 新增：同步版本
    toggleFilter: (filterName: FilterName, value: string) => void;
    resetFilters: () => void;
    sortOrder: SortOrder;
    setSortOrder: (sortOrder: SortOrder) => void;
    setCurrentPage: (page: number) => void;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
    counts: Counts;
    refetch: () => void; // refetches all Resource Library Items
    initialize: () => void;
}

const ResourceLibraryItemContext = createContext<ResourceLibraryItemContextValue | undefined>(
    undefined
);

interface ResourceLibraryItemProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
    itemsPerPage?: number;
}

const ResourceLibraryItemsProvider = ({
    children,
    apolloClient,
    itemsPerPage = 10,
}: ResourceLibraryItemProviderProps) => {
    const { locale } = useLocale();

    // Use the unsafe hooks to get dependent providers' data without triggering their initialization.
    const { productGroupById, initialize: initializeProductGroups } = useProductGroupsUnsafe();
    const { productApplicationById, initialize: initializeProductApplications } =
        useProductApplicationsUnsafe();
    const { resourceTypeById, initialize: initializeResourceTypes } = useResourceTypesUnsafe();
    const { navigationNodeById, initialize: initializeNavigationNodes } =
        useNavigationNodesUnsafe();
    const {
        items: resourceLibraryArticleItems,
        loading: loadingArticleItems,
        error: articleItemsError,
        initialize: initializeArticleItems,
    } = useResourceLibraryArticleItemsUnsafe();
    const {
        items: resourceLibraryNewsItems,
        loading: loadingNewsItems,
        error: newsItemsError,
        initialize: initializeNewsItems,
    } = useResourceLibraryNewsItemsUnsafe();
    const {
        items: resourceLibraryNpiItems,
        loading: loadingNpiItems,
        error: npiItemsError,
        initialize: initializeNpiItems,
    } = useResourceLibraryNewProductIntroductionItemsUnsafe();
    const {
        items: resourceLibrarySitePageItems,
        loading: loadingSitePageItems,
        error: sitePageItemsError,
        initialize: initializeSitePageItems,
    } = useResourceLibrarySitePageItemsUnsafe();

    const initializeDependentProviders = useCallback(() => {
        initializeProductGroups();
        initializeProductApplications();
        initializeResourceTypes();
        initializeNavigationNodes();
        initializeArticleItems();
        initializeNewsItems();
        initializeNpiItems();
        initializeSitePageItems();
    }, [
        initializeArticleItems,
        initializeNavigationNodes,
        initializeNewsItems,
        initializeNpiItems,
        initializeProductApplications,
        initializeProductGroups,
        initializeResourceTypes,
        initializeSitePageItems,
    ]);

    const [filters, setFilters] = useState<Filters>({
        brandId: null,
        productGroupIds: [],
        productApplicationIds: [],
        resourceTypeIds: [],
        navigationScopeIds: [],
        assetPaths: [],
        searchTerm: null,
    });
    const [sortOrder, setSortOrder] = useState<SortOrder>('latest');
    const [currentPage, setCurrentPage] = useState<number>(1);

    const [initialized, setInitialized] = useState(false);

    const initialize = useCallback(() => {
        if (!initialized) {
            setInitialized(true);
            initializeDependentProviders();
        }
    }, [initialized, initializeDependentProviders]);

    const assetItemsQuery = GET_RESOURCE_LIBRARY_ASSET_ITEMS_QUERY;
    const {
        loading: loadingAssetItems,
        error: assetItemsError,
        data: assetItemsData,
    } = useQuery<GetResourceLibraryAssetItemListingQuery>(assetItemsQuery, {
        variables: getResourceLibraryAssetItemsQueryVariables(locale),
        client: apolloClient,
        errorPolicy: 'all', // Allow partial data instead of full rejection
        skip: !initialized, // Do not run query until initialization is triggered.
    });

    const {
        fallbackData: assetItemsFallbackData,
        loading: loadingAssetItemsFallback,
        error: assetItemsFallbackError,
    } = useQueryFallbackResponse({
        query: assetItemsQuery,
        locale,
        initialized,
        queryError: assetItemsError,
    });

    // load InternalLinkItems
    const internaLinkItemsQuery = GET_RESOURCE_LIBRARY_INTERNAL_LINK_ITEMS_QUERY;
    const {
        loading: loadingInternaLinkItems,
        error: internaLinkItemsError,
        data: internalLinkItemsData,
    } = useQuery<GetResourceLibraryInternalLinkItemListingQuery>(internaLinkItemsQuery, {
        variables: getResourceLibraryInternalLinkItemsQueryVariables(locale),
        client: apolloClient,
        errorPolicy: 'all', // Allow partial data instead of full rejection
        skip: !initialized, // Do not run query until initialization is triggered.
    });

    const {
        fallbackData: internalLinkItemsFallbackData,
        loading: loadingInternalLinkItemsFallback,
        error: internalLinkItemsFallbackError,
    } = useQueryFallbackResponse({
        query: internaLinkItemsQuery,
        locale,
        initialized,
        queryError: internaLinkItemsError,
    });

    useEffect(() => {
        if (internaLinkItemsError) {
            console.error(
                'ResourceLibraryItemsProvider asset items query error',
                internaLinkItemsError
            );
        }
    }, [internaLinkItemsError]);

    const refetch = useCallback(() => {
        if (initialized) {
            apolloClient.refetchQueries({
                include: 'active',
            });
        } else {
            initialize();
        }
    }, [apolloClient, initialize, initialized]);

    // Transform data when query is completed
    const resourceLibraryAssetItems = useMemo(() => {
        const source = assetItemsFallbackData ?? assetItemsData;
        if (!source) {
            return [];
        }
        return transformResourceLibraryAssetItemsResponse(source, locale);
    }, [assetItemsData, assetItemsFallbackData, locale]);

    const resourceLibraryInternalLinkItems = useMemo(() => {
        const source = internalLinkItemsFallbackData ?? internalLinkItemsData;
        if (!source) {
            return [];
        }
        return transformResourceLibraryInternalLinkItemsResponse(source);
    }, [internalLinkItemsData, internalLinkItemsFallbackData]);

    const resourceLibraryItems = useMemo(() => {
        const resourceLibraryItems: ResourceLibraryItem[] = [];
        resourceLibraryItems.push(...resourceLibraryAssetItems);
        resourceLibraryItems.push(...resourceLibraryInternalLinkItems);
        resourceLibraryItems.push(...resourceLibraryArticleItems);
        resourceLibraryItems.push(...resourceLibraryNewsItems);
        resourceLibraryItems.push(...resourceLibraryNpiItems);
        resourceLibraryItems.push(...resourceLibrarySitePageItems);
        return resourceLibraryItems;
    }, [
        resourceLibraryAssetItems,
        resourceLibraryInternalLinkItems,
        resourceLibraryArticleItems,
        resourceLibraryNewsItems,
        resourceLibraryNpiItems,
        resourceLibrarySitePageItems,
    ]);

    // Apply filtering, searching, and sorting on the client side
    const filteredItemsWithRelevance = useMemo((): SearchResult[] => {
        return filterItemsWithRelevance({
            items: resourceLibraryItems,
            filters,
            sortOrder,
            filterResolvers: {
                productGroupById,
                productApplicationById,
                resourceTypeById,
                navigationNodeById,
            },
        });
    }, [
        resourceLibraryItems,
        filters,
        sortOrder,
        productGroupById,
        productApplicationById,
        resourceTypeById,
        navigationNodeById,
    ]);

    // Get filtered items
    const filteredItems = useMemo(() => {
        return filteredItemsWithRelevance.map((result) => result.item);
    }, [filteredItemsWithRelevance]);

    // Create a map of filtered items with relevance scores and missing terms
    const filteredItemsRelevanceMap = useMemo((): FilteredItemsRelevanceMap => {
        const relevanceMap = new Map<string, SearchResult>(
            filteredItemsWithRelevance.map((result) => [result.item.id, result])
        );
        return relevanceMap;
    }, [filteredItemsWithRelevance]);

    // Calculate pagination
    const totalPages = useMemo(() => {
        if (!filteredItems) return 0;
        return Math.ceil(filteredItems.length / itemsPerPage);
    }, [filteredItems, itemsPerPage]);

    // Get items for the current page
    const paginatedItems = useMemo(() => {
        if (!filteredItems) return [];
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredItems.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredItems, currentPage, itemsPerPage]);

    // Set currentPage to 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters]);

    // Calculates item counts for brands, product groups, product applications, and resource types
    const counts = useMemo<Counts>(
        () =>
            getCounts(
                filterItemsUsingEmptyFilters(resourceLibraryItems),
                productGroupById,
                productApplicationById,
                resourceTypeById
            ),
        [resourceLibraryItems, productGroupById, productApplicationById, resourceTypeById]
    );

    const setFilter = useCallback(<K extends FilterName>(filterName: K, value: Filters[K]) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [filterName]: value,
        }));
    }, []);

    /* ------------------------------------------------------------------ */
    /* 🔸 setFilterSync：同步更新 filters 並「馬上」算出最新 filteredItems  */
    /* ------------------------------------------------------------------ */
    const setFilterSync = useCallback(
        <K extends FilterName>(filterName: K, value: Filters[K]): ResourceLibraryItem[] => {
            // 1. 預先組出「下一版 filters」以便重算
            const nextFilters: Filters = { ...filters, [filterName]: value };

            // 2. 用跟 Provider 相同的 util 立即重算
            const nextResults = filterItemsWithRelevance({
                items: resourceLibraryItems,
                filters: nextFilters,
                sortOrder,
                filterResolvers: {
                    productGroupById,
                    productApplicationById,
                    resourceTypeById,
                    navigationNodeById,
                },
            }).map((r) => r.item);

            // 3. 真的更新 React state（仍是 async，給 UI 用）
            setFilters(nextFilters);

            // 4. 把「新結果」回傳給呼叫端
            return nextResults;
        },
        [
            filters,
            productApplicationById,
            productGroupById,
            resourceLibraryItems,
            resourceTypeById,
            navigationNodeById,
            sortOrder,
        ]
    );

    // Toggle given filter value (add/remove) if the filter is an array or set/unset if it's
    // a single value
    const toggleFilter = useCallback(<K extends FilterName>(filterName: K, value: string) => {
        setFilters((prevFilters) => {
            const currentFilterValue = prevFilters[filterName];

            if (filterDescriptors[filterName].isArray) {
                // Handle array filters
                const currentArray = currentFilterValue as string[];
                return {
                    ...prevFilters,
                    [filterName]: toggleValueInArray(currentArray, value),
                };
            } else {
                // Handle single-value filters
                const currentValue = currentFilterValue as string | null;
                return {
                    ...prevFilters,
                    [filterName]: currentValue === value ? null : value,
                };
            }
        });
    }, []);

    const resetFilters = useCallback(() => {
        setFilters({
            brandId: null,
            productGroupIds: [],
            productApplicationIds: [],
            resourceTypeIds: [],
            navigationScopeIds: [],
            assetPaths: [],
            searchTerm: null,
        });
    }, []);

    const loading =
        loadingAssetItems ||
        loadingInternaLinkItems ||
        loadingArticleItems ||
        loadingNewsItems ||
        loadingNpiItems ||
        loadingSitePageItems ||
        loadingAssetItemsFallback ||
        loadingInternalLinkItemsFallback;

    // FIXME: We use only first error
    const error =
        assetItemsError ||
        internaLinkItemsError ||
        articleItemsError ||
        newsItemsError ||
        npiItemsError ||
        sitePageItemsError ||
        assetItemsFallbackError ||
        internalLinkItemsFallbackError;

    const contextValue = useMemo(
        () => ({
            items: resourceLibraryItems || [],
            filteredItems,
            paginatedItems,
            filteredItemsRelevanceMap,
            loading,
            error,
            filters,
            setFilter,
            toggleFilter,
            resetFilters,
            sortOrder,
            setSortOrder,
            setCurrentPage,
            totalPages,
            currentPage,
            itemsPerPage,
            counts,
            refetch,
            initialize,
            setFilterSync,
        }),
        [
            resourceLibraryItems,
            filteredItems,
            paginatedItems,
            filteredItemsRelevanceMap,
            loading,
            error,
            filters,
            setFilter,
            toggleFilter,
            resetFilters,
            sortOrder,
            setSortOrder,
            setCurrentPage,
            totalPages,
            currentPage,
            itemsPerPage,
            counts,
            refetch,
            initialize,
            setFilterSync,
        ]
    );

    return (
        <ResourceLibraryItemContext.Provider value={contextValue}>
            {children}
        </ResourceLibraryItemContext.Provider>
    );
};

// Unsafe hook: does not trigger initialization.
function useResourceLibraryItemsUnsafe() {
    const context = useContext(ResourceLibraryItemContext);
    if (!context) {
        throw new Error(
            'useResourceLibraryItems must be used within a ResourceLibraryItemsProvider'
        );
    }
    return context;
}

// Safe hook: triggers initialization for this provider, but does not trigger initialization for dependent providers.
function useResourceLibraryItems() {
    const context = useResourceLibraryItemsUnsafe();
    useEffect(() => {
        context.initialize();
    }, [context]);
    return context;
}

export { ResourceLibraryItemsProvider, useResourceLibraryItems, useResourceLibraryItemsUnsafe };
export type { ResourceLibraryItemContextValue };
